import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../constants';
import { User } from '../types/UserTypes';

interface UserController {
    user: User | null;
    loading: boolean;
    error: string | null;
    fetchUser: () => Promise<void>;
}

// Create the context with UserController type
const UserContext = createContext<UserController | undefined>(undefined);

// Provider component
export const UserProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchUser = async () => {
        console.log("Fetching user data...");
        setLoading(true);
        try {
            const response = await axios.get(`${API_BASE_URL}/profile/`);
            console.log("User data received:", response.data);
            setUser(response.data);
            setLoading(false);
        } catch (error) {
            console.error("Error fetching user data:", error);
            setError(error instanceof Error ? error.message : "Failed to fetch user data");
            setLoading(false);
        }
    };

    // Automatically fetch user data when the provider mounts
    useEffect(() => {
        console.log("UserProvider mounted, checking for token...");
        const token = localStorage.getItem("token");
        if (token) {
            console.log("Token found, fetching user data...");
            console.log("Checking for token in localStorage...");
            console.log("Token found:", token);
            fetchUser();
        } else {
            console.log("No token found, user not authenticated");
            setLoading(false);
        }
    }, []);

    const userController: UserController = {
        user,
        loading,
        error,
        fetchUser
    };

    return (
        <UserContext.Provider value={userController}>
            {children}
        </UserContext.Provider>
    );
};

// Custom hook to use the user context
export const useUser = () => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUserContext must be used within a UserProvider');
    }
    return context;
};
